import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { installationApi, installerApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Search, Eye, MapPin, Calendar } from 'lucide-react';
import { format } from 'date-fns';

const statusConfig = {
  scheduled: { label: 'Scheduled', class: 'bg-blue-100 text-blue-800 border border-blue-200' },
  in_progress: { label: 'In Progress', class: 'bg-yellow-100 text-yellow-800 border border-yellow-200' },
  completed: { label: 'Completed', class: 'bg-green-100 text-green-800 border border-green-200' },
};

export default function Installations() {
  const { user, isInstaller } = useAuth();
  const [installations, setInstallations] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterInstaller, setFilterInstaller] = useState('all');

  const fetchData = async () => {
    try {
      const installerId = isInstaller ? user?.id : null;
      const [installationsRes, installersRes] = await Promise.all([
        installationApi.getAll(null, installerId),
        installerApi.getAll()
      ]);
      setInstallations(installationsRes.data);
      setInstallers(installersRes.data);
    } catch (error) {
      console.error('Error fetching installations:', error);
      toast.error('Failed to load installations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Data fetch runs once on mount
  }, []);

  const filteredInstallations = installations.filter(inst => {
    const matchesSearch = 
      inst.installation_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inst.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inst.customer_address?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || inst.status === filterStatus;
    const matchesInstaller = filterInstaller === 'all' || inst.installer_id === filterInstaller;
    return matchesSearch && matchesStatus && matchesInstaller;
  });

  const getStatusCounts = () => {
    const counts = { all: installations.length };
    installations.forEach(i => {
      counts[i.status] = (counts[i.status] || 0) + 1;
    });
    return counts;
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading installations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="installations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold font-heading">Installations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Schedule and track installations
          </p>
        </div>
        {!isInstaller && (
          <Link to="/installations/new">
            <Button className="rounded-sm w-full sm:w-auto" data-testid="btn-create-installation">
              <Plus className="w-4 h-4 mr-2" />
              Schedule Installation
            </Button>
          </Link>
        )}
      </div>

      {/* Status Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'scheduled', 'in_progress', 'completed'].map((status) => (
          <Button
            key={status}
            variant={filterStatus === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus(status)}
            className="rounded-sm"
            data-testid={`filter-status-${status}`}
          >
            {status === 'all' ? 'All' : statusConfig[status]?.label}
            <span className="ml-1.5 text-xs opacity-70">({statusCounts[status] || 0})</span>
          </Button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by number, customer, or address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="installation-search"
          />
        </div>
        {!isInstaller && (
          <Select value={filterInstaller} onValueChange={setFilterInstaller}>
            <SelectTrigger className="w-full sm:w-[200px] rounded-sm" data-testid="filter-installer">
              <SelectValue placeholder="Filter by installer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Installers</SelectItem>
              {installers.map(installer => (
                <SelectItem key={installer.id} value={installer.id}>{installer.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Installations Table */}
      <Card className="border border-border shadow-none rounded-sm">
        <CardContent className="p-0">
          {filteredInstallations.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">No installations found</p>
              {!isInstaller && (
                <Link to="/installations/new">
                  <Button variant="link" className="mt-2">
                    Schedule your first installation
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="installations-table">
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="font-medium">Number</TableHead>
                    <TableHead className="font-medium">Customer</TableHead>
                    <TableHead className="font-medium hidden md:table-cell">Address</TableHead>
                    <TableHead className="font-medium">Date</TableHead>
                    <TableHead className="font-medium hidden sm:table-cell">Installer</TableHead>
                    <TableHead className="font-medium">Status</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInstallations.map((inst) => (
                    <TableRow key={inst.id} data-testid={`installation-row-${inst.id}`}>
                      <TableCell className="font-mono-data font-medium">{inst.installation_number}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{inst.customer_name}</p>
                          <p className="text-xs text-muted-foreground md:hidden flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {inst.customer_address?.substring(0, 30)}...
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground hidden md:table-cell">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 shrink-0" />
                          <span className="truncate max-w-[200px]">{inst.customer_address}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {inst.installation_date ? format(new Date(inst.installation_date), 'dd MMM yyyy') : '-'}
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">{inst.installer_name}</TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig[inst.status]?.class} rounded-none text-xs uppercase tracking-wider`}>
                          {statusConfig[inst.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Link to={`/installations/${inst.id}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`btn-view-installation-${inst.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
